// NewSessionDialog.tsx — M2 (FE_SPEC §5) refactor.
//
// Pre-M2 (M0/M0.5): onSubmit(topic, intentPreset?) — positional args.
// M2 widens to object shape so attachments[] can ride along once FE-A's
// AttachmentUploader lands. Backwards-compat at the **call site** is the
// caller's responsibility (Dashboard.tsx adapter shim).
//
// PR-UX-10 flow · replaces window.prompt (browser-native interrupts flow).
// PR-UX-04 4 states · default / typing / submitting / error.
// PR-UX-08 molecule · ConfirmDialog-shaped but with form input.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../atoms/Button';
import Field from '../atoms/Field';
import type { IntentPreset } from '@/types/intent';
import type { AttachmentInput } from '@/api/m0_sessions';

// ★ M2 onSubmit payload (FE_SPEC §5.1).
export interface NewSessionSubmitPayload {
  topic: string;
  intent_preset?: IntentPreset;
  attachments: AttachmentInput[];
  // Pass-through hooks for FE-A AttachmentUploader (P0-3 / P0-6 / P0-7
  // error rerouting handled by parent based on resp.error.code).
}

interface NewSessionDialogProps {
  open: boolean;
  busy?: boolean;
  error?: string | null;
  /**
   * ★ M2.1 W1: optional pre-rendered inline error node (e.g. ErrorInlineWithRetry).
   * When provided, replaces the legacy single-line `error` string banner with a
   * richer inline component (retry button · hint · "不会扣费" variant). Parent
   * (Dashboard) maps wire error codes → component variant and passes the
   * fully-constructed React node here.
   */
  inlineErrorNode?: React.ReactNode | null;
  minLength?: number;
  /**
   * ★ M2 (FE_SPEC §5.1): onSubmit now receives an object so attachments[]
   * + intent_preset can ride along. Legacy callers must wrap to the object
   * form (Dashboard.tsx already adapted).
   */
  onSubmit: (payload: NewSessionSubmitPayload) => void;
  onCancel: () => void;
  /**
   * ★ M2 slot for FE-A AttachmentUploader (FE_SPEC §5.2). Optional render
   * prop so the dialog stays usable while FE-A finishes the component.
   * When provided, dialog disables submit while any attachment status
   * `=== 'uploading'` (parent owns AttachmentInput[] state via render prop).
   */
  attachmentSlot?: (
    value: AttachmentInput[],
    onChange: (next: AttachmentInput[]) => void,
    disabled: boolean,
  ) => React.ReactNode;
}

export default function NewSessionDialog({
  open,
  busy = false,
  error,
  inlineErrorNode,
  minLength = 50, // ★ M2 default 50 (PRD §1 + §4 旅程 quote · was 6 in M0).
  onSubmit,
  onCancel,
  attachmentSlot,
}: NewSessionDialogProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentInput[]>([]);

  useEffect(() => {
    if (!open) {
      setValue('');
      setTouched(false);
      setAttachments([]);
      return;
    }
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const trimmed = value.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < minLength;
  const inlineError = touched && tooShort
    ? t('newSession.tooShort_50', { min: minLength })
    : undefined;
  const submitDisabled = trimmed.length < minLength;

  const submit = () => {
    setTouched(true);
    if (submitDisabled) return;
    onSubmit({
      topic: trimmed,
      // intent_preset omitted · BE LLM classifier picks the direction
      // (FYD-2026-0512 UX: dropdown removed to declutter the new-session
      // dialog · auto-classify is the default in 99% of sessions anyway).
      attachments,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ns-title"
      className="fixed inset-0 z-[100] flex items-center justify-center px-6 bg-ink/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="bg-paper border border-hairline rounded-lg shadow-2xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
        <h2 id="ns-title" className="font-serif text-2xl font-medium mb-2">
          {t('newSession.title')}
        </h2>
        <p className="text-sm text-ink-2 mb-6">{t('newSession.sub')}</p>

        {/* ★ M2.1 W1: inline error slot (ErrorInlineWithRetry node from parent) */}
        {inlineErrorNode}

        <div className="space-y-4">
          <Field
            as="textarea"
            ref={inputRef as unknown as React.Ref<HTMLInputElement | HTMLTextAreaElement>}
            label={t('newSession.label')}
            hint={t('newSession.hint')}
            error={inlineError ?? error ?? undefined}
            required
            rows={8}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setValue(e.target.value);
              if (!touched) setTouched(true);
            }}
            onBlur={() => setTouched(true)}
            maxLength={20000}
          />
          {/* ★ M2 AttachmentUploader slot (FE-A 提供 · 父级 render prop) */}
          {attachmentSlot && attachmentSlot(attachments, setAttachments, busy)}
        </div>
        <p className="text-xs font-mono tracking-meta uppercase text-ink-3 mt-2 text-right">
          {trimmed.length.toLocaleString()} / 20,000 · 至少 {minLength} 字
        </p>

        <div className="flex gap-3 justify-end mt-6">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={busy}
            disabled={submitDisabled || busy}
          >
            {t('newSession.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
